<template>
    <input
        type="text"
        class="form-control period-select"
        :value="displayValue"
        :class="$attrs.class"
        @input="onInput"
        @blur="onBlur"
        @focus="$emit('focus', $event)"
    >
</template>

<script>
import flatpickr from 'flatpickr'
import { Czech } from 'flatpickr/dist/l10n/cs.js'
import 'flatpickr/dist/flatpickr.css'
import moment from 'moment'

export default {
    name: 'DatePicker',
    inheritAttrs: false,
    compatConfig: {
        COMPONENT_V_MODEL: false,
        INSTANCE_ATTRS_CLASS_STYLE: false
    },
    props: {
        value: {
            default: null
        },
        modelValue: {
            default: undefined
        },
        config: {
            type: Object,
            default: () => ({})
        }
    },
    emits: ['input', 'update:modelValue', 'focus', 'blur'],
    data () {
        return {
            picker: null
        }
    },
    computed: {
        currentValue () {
            return this.modelValue !== undefined ? this.modelValue : this.value
        },
        format () {
            return (this.config && this.config.format) || 'DD.MM.YYYY'
        },
        pickerFormat () {
            return this.format
                .replace(/YYYY/g, 'Y')
                .replace(/YY/g, 'y')
                .replace(/DD/g, 'd')
                .replace(/MM/g, 'm')
        },
        pickerLocale () {
            return this.config && this.config.locale === 'cs'
                ? Czech
                : (this.config && this.config.locale) || 'default'
        },
        displayValue () {
            if (this.currentValue == null || this.currentValue === '') return ''
            if (moment.isMoment(this.currentValue)) {
                return this.currentValue.isValid() ? this.currentValue.format(this.format) : ''
            }
            if (typeof this.currentValue === 'string') {
                return this.currentValue
            }
            const m = moment(this.currentValue)
            return m.isValid() ? m.format(this.format) : ''
        }
    },
    watch: {
        currentValue (value) {
            if (!this.picker) return

            if (value == null || value === '') {
                if (this.$el.value !== '') {
                    this.picker.clear(false)
                }
                return
            }

            const date = this.toDate(value)
            if (date && this.$el.value !== moment(date).format(this.format)) {
                this.picker.setDate(date, false)
            }
        }
    },
    mounted () {
        this.picker = flatpickr(this.$el, {
            allowInput: true,
            dateFormat: this.pickerFormat,
            defaultDate: this.toDate(this.currentValue),
            locale: this.pickerLocale,
            minDate: this.toDate(this.config && this.config.minDate),
            maxDate: this.toDate(this.config && this.config.maxDate)
        })
    },
    beforeUnmount () {
        this.destroyPicker()
    },
    beforeDestroy () {
        this.destroyPicker()
    },
    methods: {
        parse (str) {
            const m = moment(str, this.format, true)
            if (!m.isValid()) {
                return moment(str, this.format)
            }
            return m
        },
        toDate (value) {
            if (value == null || value === '') return null
            if (moment.isMoment(value)) {
                return value.isValid() ? value.toDate() : null
            }
            if (typeof value === 'string') {
                const parsed = this.parse(value)
                return parsed.isValid() ? parsed.toDate() : null
            }
            const parsed = moment(value)
            return parsed.isValid() ? parsed.toDate() : null
        },
        clamp (m) {
            if (!m || !m.isValid()) return m
            if (this.config && this.config.maxDate) {
                const max = moment(this.config.maxDate)
                if (m.isAfter(max)) return max.clone()
            }
            if (this.config && this.config.minDate) {
                const min = moment(this.config.minDate)
                if (m.isBefore(min)) return min.clone()
            }
            return m
        },
        onInput (e) {
            const str = e.target.value
            const parsed = this.parse(str)
            this.emitValue(parsed.isValid() ? this.clamp(parsed) : str)
        },
        onBlur (e) {
            const parsed = this.parse(e.target.value)
            if (parsed.isValid()) {
                this.emitValue(this.clamp(parsed))
            }
            this.$emit('blur', e)
        },
        emitValue (value) {
            this.$emit('input', value)
            this.$emit('update:modelValue', value)
        },
        destroyPicker () {
            if (this.picker) {
                this.picker.destroy()
                this.picker = null
            }
        }
    }
}
</script>
